import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  WhatsappLogo, LockKey, CheckCircle, Warning, Eye, EyeSlash,
} from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function checkStrength(pwd) {
  const checks = [
    { label: "Pelo menos 8 caracteres", pass: pwd.length >= 8 },
    { label: "1 letra maiúscula", pass: /[A-Z]/.test(pwd) },
    { label: "1 número", pass: /\d/.test(pwd) },
  ];
  return { ok: checks.every((c) => c.pass), checks };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, email: null, error: null });
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, email: null, error: "Link inválido. Peça um novo link de recuperação." });
      return;
    }
    axios.get(`${API}/auth/reset-password/verify?token=${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, email: r.data.email, error: null }))
      .catch((err) => setState({
        loading: false, email: null,
        error: err?.response?.data?.detail || "Link inválido ou expirado.",
      }));
  }, [token]);

  const { ok: strong, checks } = checkStrength(newPwd);
  const matches = newPwd.length > 0 && newPwd === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!strong) return toast.error("A senha não atende aos requisitos.");
    if (!matches) return toast.error("As senhas não coincidem.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPwd });
      setDone(true);
      toast.success("Senha alterada com sucesso!");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#0F4C3A] text-white flex items-center justify-center">
            <WhatsappLogo size={20} weight="bold" />
          </div>
          <span className="font-bold tracking-tight text-lg">NETSZAP</span>
        </div>

        <div className="bg-white border-2 border-[#E5E5E0] p-8">
          {state.loading ? (
            <p className="text-center text-sm text-[#5C5C59] py-8">Verificando link…</p>
          ) : state.error ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-[#B91C1C] text-white flex items-center justify-center rounded-full mx-auto mb-4">
                <Warning size={28} weight="fill" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter mb-2">Link inválido</h1>
              <p className="text-sm text-[#5C5C59] mb-6">{state.error}</p>
              <Link
                to="/forgot-password"
                className="inline-block bg-[#0F4C3A] hover:bg-[#0A3629] text-white px-6 py-3 text-sm font-semibold"
                data-testid="rp-request-new"
              >
                Solicitar novo link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-[#0F4C3A] text-white flex items-center justify-center rounded-full mx-auto mb-5">
                <CheckCircle size={32} weight="fill" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter mb-2">Senha alterada!</h1>
              <p className="text-sm text-[#5C5C59] mb-6">
                Sua nova senha já está valendo. Redirecionando para o login…
              </p>
              <Link
                to="/login"
                className="inline-block bg-[#0F4C3A] hover:bg-[#0A3629] text-white px-6 py-3 text-sm font-semibold"
                data-testid="rp-goto-login"
              >
                Ir para o login agora
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 bg-[#0F4C3A] text-white flex items-center justify-center">
                  <LockKey size={22} weight="bold" />
                </div>
                <div>
                  <h1 className="text-xl font-black tracking-tighter">Criar nova senha</h1>
                  <p className="text-xs text-[#5C5C59]">{state.email}</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest">Nova senha</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPwd ? "text" : "password"}
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      required
                      autoFocus
                      autoComplete="new-password"
                      data-testid="rp-new"
                      className="rounded-none pr-10"
                    />
                    <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5C59]">
                      {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {newPwd.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {checks.map((c, i) => (
                        <li key={i} className={`flex items-center gap-2 text-xs ${c.pass ? "text-[#0F4C3A]" : "text-[#B91C1C]"}`}>
                          {c.pass ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                          {c.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest">Confirmar nova senha</Label>
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                    data-testid="rp-confirm"
                    className="rounded-none mt-1"
                  />
                  {confirm.length > 0 && (
                    <p className={`mt-1 text-xs flex items-center gap-1 ${matches ? "text-[#0F4C3A]" : "text-[#B91C1C]"}`}>
                      {matches ? <CheckCircle size={12} weight="fill" /> : <Warning size={12} weight="fill" />}
                      {matches ? "As senhas coincidem" : "As senhas não coincidem"}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !strong || !matches}
                  className="w-full rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white h-11 disabled:opacity-40"
                  data-testid="rp-submit"
                >
                  {submitting ? "Salvando…" : "Redefinir senha"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
